/**
 * Admin Sanction Form Component — Wizard Style
 * Multi-step modal form for creating/editing administrative sanctions with works
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { adminSanctionsApi, worksApi } from '../../services/wmsService';
import FundingPatternEditor, { type FundingPatternEditorHandle } from '../FundingPatternEditor';
import { useToast } from '@/hooks/useToast';
import { useWorkMasterList } from '../../hooks/useWorkMasterList';
import { ListSkeleton, TableSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_GRADIENT, WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface WorkRow {
  id: string;
  // When set, this row corresponds to an existing work record (do not create on save; link instead)
  wrk_id?: number;
  wrk_cd?: string;
  ulb_nm?: string;
  fy_cd?: string;
  estmt_cd?: string;
  wrk_type_nm?: string;
  schme_nm?: string;
  wrk_nm: string;
  wrk_type_id: number | null;
  wrk_ctgry_id: number | null;
  schme_id: number | null;
  estmt_cst_at: number;
  snctn_at: number;
  prty_nu: number | null;
  snctn_rmrks_tx: string;
}

interface SanctionAuthority {
  snctn_auth_id: number;
  snctn_auth_cd: string;
  snctn_auth_nm: string;
}

interface PreSelectedWork {
  wrk_id: number;
  wrk_cd: string;
  wrk_nm: string;
  wrk_type_id?: number | null;
  wrk_type_nm?: string;
  wrk_ctgry_id?: number | null;
  schme_id?: number | null;
  schme_nm?: string;
  fy_id?: number | null;
  ulb_nm?: string;
  snctn_at?: number;
  orgnl_estmt_at?: number;
}

interface AdminSanctionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editId?: number | null;
  preSelectedWorks?: PreSelectedWork[];
  preSelectedFyId?: number | null;
}

const STEPS = [
  { key: 'details', label: 'Sanction Details', icon: 'ri-file-list-3-line' },
  { key: 'works', label: 'Works', icon: 'ri-building-2-line' },
  { key: 'funding', label: 'Funding Pattern', icon: 'ri-money-rupee-circle-line' },
  { key: 'review', label: 'Allocation & Review', icon: 'ri-check-double-line' },
];

const emptyWork = (defaultSchmeId?: number | null): WorkRow => ({
  id: crypto.randomUUID(),
  wrk_nm: '',
  wrk_type_id: null,
  wrk_ctgry_id: null,
  schme_id: defaultSchmeId || null,
  estmt_cst_at: 0,
  snctn_at: 0,
  prty_nu: null,
  snctn_rmrks_tx: ''
});

const fmtINR = (n: number) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

export default function AdminSanctionForm({ isOpen, onClose, onSuccess, editId, preSelectedWorks, preSelectedFyId }: AdminSanctionFormProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [step, setStep] = useState(0);

  // Form state
  const [admn_snctn_nm, setTitle] = useState('');
  const [dscn_tx, setDescription] = useState('');
  const [fy_id, setFyId] = useState<number | null>(null);
  const [snctn_auth_id, setSanctionAuthId] = useState<number | null>(null);
  const [schme_id, setSchmeId] = useState<number | null>(null);
  const [go_nu, setGoNu] = useState('');
  const [go_dt, setGoDt] = useState('');
  const [go_doc_file, setGoDocFile] = useState<File | null>(null);
  const [rsln_nu, setRslnNu] = useState('');
  const [rsln_dt, setRslnDt] = useState('');
  const [rmrks_tx, setRmrksTx] = useState('');
  const [iec_pct, setIecPct] = useState<number>(1);
  const [iec_at, setIecAt] = useState<number>(0);
  const [cb_pct, setCbPct] = useState<number>(1);
  const [cb_at, setCbAt] = useState<number>(0);
  const [works, setWorks] = useState<WorkRow[]>([]);

  /* The Funding Pattern step keeps its edits inside FundingPatternEditor, whose only
   * save trigger was its own button. A user who edited the amounts and pressed Next
   * lost them: the step unmounted, nothing was written, and the Funding Pattern grid
   * and Tender Preparation both kept showing "-" for a work whose split had been
   * entered. Flushing the editor on Next (and on final save) closes that gap without
   * duplicating the fields anywhere else — the record stays the single source of
   * truth that Tender Preparation already reads. */
  const fundingRef = useRef<FundingPatternEditorHandle>(null);
  const flushFundingPattern = async (): Promise<boolean> => {
    if (!fundingRef.current?.hasUnsavedChanges()) return true;
    return fundingRef.current.save();
  };

  // Masters
  const { workTypes, workCategories, schemes, financialYears } = useWorkMasterList();
  const [authorities, setAuthorities] = useState<SanctionAuthority[]>([]);

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({});
  const skipAutoCalcRef = useRef(false);

  // Picker for existing works without admin sanction
  interface UnsanctionedWork {
    wrk_id: number; wrk_cd: string; wrk_nm: string;
    snctn_at: string | number | null; orgnl_estmt_at: string | number | null;
    ulb_id?: number | null; ulb_nm?: string | null;
    schme_id?: number | null; schme_nm?: string | null;
    fy_id?: number | null; fy_cd?: string | null; fy_nm?: string | null;
    wrk_type_id?: number | null; wrk_type_nm?: string | null;
    wrk_ctgry_id?: number | null;
    estmt_id?: number | null; estmt_cd?: string | null; estmt_totl_at?: string | number | null;
    estmt_type_cd?: string | null; estmt_sts_cd?: string | null;
  }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerWorks, setPickerWorks] = useState<UnsanctionedWork[]>([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());
  const [pickerOnlyWithEstimate, setPickerOnlyWithEstimate] = useState(false);
  const [pickerFy, setPickerFy] = useState<number | ''>('');
  const [pickerSchme, setPickerSchme] = useState<number | ''>('');
  const [pickerType, setPickerType] = useState<number | ''>('');
  const [pickerUda, setPickerUda] = useState<number | ''>('');

  const isEdit = !!editId;

  // Load sanction authorities
  useEffect(() => {
    if (!isOpen) return;
    adminSanctionsApi.getAuthorities()
      .then(data => setAuthorities(data || []))
      .catch(error => console.error('Error loading authorities:', error));
  }, [isOpen]);

  // Load existing data if editing
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setStep(0);
    if (!editId) { resetForm(); return; }
    const loadSanction = async () => {
      setLoading(true);
      try {
        const data = await adminSanctionsApi.getById(editId);
        if (data) {
          setTitle(data.as_nm || '');
          setDescription(data.dscn_tx || '');
          setFyId(data.fy_id || null);
          setSanctionAuthId(data.snctn_auth_id || null);
          setGoNu(data.go_nu || '');
          setGoDt(data.go_dt ? String(data.go_dt).split('T')[0] : '');
          setRslnNu(data.rsln_nu || '');
          setRslnDt(data.rsln_dt ? String(data.rsln_dt).split('T')[0] : '');
          setRmrksTx(data.rmrks_tx || '');
          skipAutoCalcRef.current = true;
          if (data.works?.length) {
            // Derive top-level scheme from the most common scheme across works
            const schmeIds = data.works.map((w: any) => w.schme_id).filter(Boolean);
            if (schmeIds.length > 0) {
              const freq = new Map<number, number>();
              schmeIds.forEach((id: number) => freq.set(id, (freq.get(id) || 0) + 1));
              const mostCommon = [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
              setSchmeId(mostCommon);
            }
            setWorks(data.works.map((w: any) => ({
              id: String(w.wrk_id),
              wrk_id: Number(w.wrk_id),
              wrk_cd: w.wrk_cd,
              ulb_nm: w.ulb_nm,
              fy_cd: w.fy_cd || w.fy_nm,
              wrk_type_nm: w.wrk_type_nm,
              schme_nm: w.schme_nm,
              wrk_nm: w.wrk_nm || '',
              wrk_type_id: w.wrk_type_id ? Number(w.wrk_type_id) : null,
              wrk_ctgry_id: w.wrk_ctgry_id ? Number(w.wrk_ctgry_id) : null,
              schme_id: w.schme_id ? Number(w.schme_id) : null,
              estmt_cst_at: Number(w.orgnl_estmt_at || w.snctn_at || 0),
              snctn_at: Number(w.snctn_at || 0),
              prty_nu: w.prty_nu || null,
              snctn_rmrks_tx: w.snctn_rmrks_tx || ''
            })));
          }
          setIecAt(Number(data.iec_at) || 0);
          setCbAt(Number(data.cb_at) || 0);
          setTimeout(() => { skipAutoCalcRef.current = false; }, 200);
        }
      } catch (error) {
        console.error('Error loading sanction:', error);
        showError('Failed to load admin sanction data');
      } finally {
        setLoading(false);
      }
    };
    loadSanction();
  }, [isOpen, editId]);

  // Populate from pre-selected works (from Pending tab)
  useEffect(() => {
    if (!isOpen || editId || !preSelectedWorks || preSelectedWorks.length === 0) return;

    const workRows: WorkRow[] = preSelectedWorks.map(w => ({
      id: String(w.wrk_id),
      wrk_id: w.wrk_id,
      wrk_cd: w.wrk_cd,
      ulb_nm: w.ulb_nm,
      wrk_type_nm: w.wrk_type_nm,
      schme_nm: w.schme_nm,
      wrk_nm: w.wrk_nm || '',
      wrk_type_id: w.wrk_type_id || null,
      wrk_ctgry_id: w.wrk_ctgry_id || null,
      schme_id: w.schme_id || null,
      estmt_cst_at: Number(w.orgnl_estmt_at || w.snctn_at || 0),
      snctn_at: Number(w.snctn_at || 0),
      prty_nu: null,
      snctn_rmrks_tx: ''
    }));
    setWorks(workRows);

    // Auto-set FY from preSelectedFyId or from the most common FY in works
    if (preSelectedFyId) {
      setFyId(preSelectedFyId);
    } else {
      const fyIds = preSelectedWorks.map(w => w.fy_id).filter(Boolean) as number[];
      if (fyIds.length > 0) {
        const freq = new Map<number, number>();
        fyIds.forEach(id => freq.set(id, (freq.get(id) || 0) + 1));
        const mostCommon = [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
        setFyId(mostCommon);
      }
    }

    // Auto-set scheme from most common scheme in works
    const schmeIds = preSelectedWorks.map(w => w.schme_id).filter(Boolean) as number[];
    if (schmeIds.length > 0) {
      const freq = new Map<number, number>();
      schmeIds.forEach(id => freq.set(id, (freq.get(id) || 0) + 1));
      const mostCommon = [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
      setSchmeId(mostCommon);
    }

    // Auto-set title
    const ulbs = [...new Set(preSelectedWorks.map(w => w.ulb_nm).filter(Boolean))];
    const schmeNames = [...new Set(preSelectedWorks.map(w => w.schme_nm).filter(Boolean))];
    setTitle(`AS - ${ulbs.length > 1 ? `${ulbs.length} UDAs` : ulbs[0] || ''} - ${schmeNames.join(', ') || ''} (${preSelectedWorks.length} works)`);
  }, [isOpen, editId, preSelectedWorks, preSelectedFyId]);

  const resetForm = () => {
    setTitle(''); setDescription(''); setFyId(null); setSanctionAuthId(null);
    setSchmeId(null); setGoNu(''); setGoDt(''); setGoDocFile(null);
    setRslnNu(''); setRslnDt(''); setRmrksTx('');
    setIecPct(1); setIecAt(0); setCbPct(1); setCbAt(0);
    setWorks([]); setErrors({}); setStep(0);
  };

  const addWork = () => setWorks(prev => [...prev, emptyWork(schme_id)]);
  const removeWork = (id: string) => setWorks(prev => prev.filter(w => w.id !== id));
  const updateWork = (id: string, field: keyof WorkRow, value: any) => {
    setWorks(prev => prev.map(w => w.id === id ? { ...w, [field]: value } : w));
  };

  // Picker: load works without admin sanction
  const openPicker = async () => {
    setPickerSelected(new Set());
    setPickerSearch('');
    setPickerFy(''); setPickerSchme(''); setPickerType(''); setPickerUda('');
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const data = await adminSanctionsApi.getUnsanctionedWorks(false);
      setPickerWorks((data || []) as UnsanctionedWork[]);
    } catch (e) {
      console.error('Error loading unsanctioned works:', e);
      showError('Failed to load unsanctioned works');
    } finally {
      setPickerLoading(false);
    }
  };

  // Distinct UDA options derived from loaded picker rows
  const pickerUlbOptions = useMemo(() => {
    const map = new Map<number, string>();
    pickerWorks.forEach((w) => { if (w.ulb_id != null) map.set(w.ulb_id, w.ulb_nm || `UDA ${w.ulb_id}`); });
    return Array.from(map.entries()).map(([id, nm]) => ({ id, nm })).sort((a, b) => a.nm.localeCompare(b.nm));
  }, [pickerWorks]);

  const filteredPickerWorks = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return pickerWorks.filter((w) => {
      if (pickerOnlyWithEstimate && !w.estmt_id) return false;
      if (pickerFy !== '' && w.fy_id !== pickerFy) return false;
      if (pickerSchme !== '' && w.schme_id !== pickerSchme) return false;
      if (pickerType !== '' && w.wrk_type_id !== pickerType) return false;
      if (pickerUda !== '' && w.ulb_id !== pickerUda) return false;
      if (!q) return true;
      return [w.wrk_cd, w.wrk_nm, w.ulb_nm, w.schme_nm, w.estmt_cd]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q));
    });
  }, [pickerWorks, pickerSearch, pickerOnlyWithEstimate, pickerFy, pickerSchme, pickerType, pickerUda]);

  const pickerFiltersActive = pickerFy !== '' || pickerSchme !== '' || pickerType !== '' || pickerUda !== '' || pickerOnlyWithEstimate || pickerSearch.trim() !== '';
  const clearPickerFilters = () => {
    setPickerFy(''); setPickerSchme(''); setPickerType(''); setPickerUda('');
    setPickerOnlyWithEstimate(false); setPickerSearch('');
  };

  const togglePickerRow = (wrkId: number) => {
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(wrkId)) next.delete(wrkId); else next.add(wrkId);
      return next;
    });
  };

  const togglePickerAll = () => {
    const eligible = filteredPickerWorks
      .filter((w) => !works.some((r) => r.id === String(w.wrk_id)))
      .map((w) => w.wrk_id);
    const allSelected = eligible.every((id) => pickerSelected.has(id));
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) eligible.forEach((id) => next.delete(id));
      else eligible.forEach((id) => next.add(id));
      return next;
    });
  };

  const confirmPicker = () => {
    if (pickerSelected.size === 0) { setPickerOpen(false); return; }
    const existingIds = new Set(works.map((r) => r.id));
    const newRows: WorkRow[] = pickerWorks
      .filter((w) => pickerSelected.has(w.wrk_id) && !existingIds.has(String(w.wrk_id)))
      .map((w) => ({
        id: String(w.wrk_id),
        wrk_id: w.wrk_id,
        wrk_cd: w.wrk_cd,
        ulb_nm: w.ulb_nm ?? undefined,
        fy_cd: w.fy_cd ?? w.fy_nm ?? undefined,
        estmt_cd: w.estmt_cd ?? undefined,
        wrk_type_nm: w.wrk_type_nm ?? undefined,
        schme_nm: w.schme_nm ?? undefined,
        wrk_nm: w.wrk_nm || '',
        wrk_type_id: w.wrk_type_id ?? null,
        wrk_ctgry_id: w.wrk_ctgry_id ?? null,
        schme_id: w.schme_id ?? schme_id ?? null,
        estmt_cst_at: Number(w.estmt_totl_at ?? w.orgnl_estmt_at ?? w.snctn_at ?? 0),
        // `??` only falls through on null/undefined, but dswms_wrk_lst_t.snctn_at is
        // "0.00" on every work (nothing in the lifecycle rolls it up), so the chain
        // stopped at zero. That zero then flowed into totalSanctionedCost and was
        // saved as the sanction's totl_snctn_at — the AS would have been created for
        // ₹0. Use || so a zero falls through to the estimate being sanctioned.
        snctn_at: Number(w.snctn_at) || Number(w.estmt_totl_at) || Number(w.orgnl_estmt_at) || 0,
        prty_nu: null,
        snctn_rmrks_tx: w.estmt_cd ? `Linked from estimate ${w.estmt_cd}` : ''
      }));
    if (newRows.length > 0) setWorks((prev) => [...prev, ...newRows]);
    showSuccess(`Added ${newRows.length} work${newRows.length === 1 ? '' : 's'} from existing list`);
    setPickerOpen(false);
    setPickerSelected(new Set());
  };

  // Totals
  const totalEstimatedCost = useMemo(() => works.reduce((s, w) => s + Number(w.estmt_cst_at || 0), 0), [works]);
  const totalSanctionedCost = useMemo(() => works.reduce((s, w) => s + Number(w.snctn_at || 0), 0), [works]);

  // Auto-calculate IEC/CB
  useEffect(() => {
    if (skipAutoCalcRef.current) return;
    if (totalSanctionedCost > 0) {
      setIecAt(Math.round(totalSanctionedCost * iec_pct / 100));
      setCbAt(Math.round(totalSanctionedCost * cb_pct / 100));
    }
  }, [totalSanctionedCost, iec_pct, cb_pct]);

  // Sector summary for review
  const sectorSummary = useMemo(() => {
    const map = new Map<string, { count: number; est: number; snctn: number }>();
    works.forEach(w => {
      const typeNm = workTypes.find(t => t.wrk_type_id === w.wrk_type_id)?.wrk_type_nm || 'Unclassified';
      const entry = map.get(typeNm) || { count: 0, est: 0, snctn: 0 };
      entry.count++; entry.est += Number(w.estmt_cst_at || 0); entry.snctn += Number(w.snctn_at || 0);
      map.set(typeNm, entry);
    });
    return Array.from(map.entries()).sort((a, b) => b[1].snctn - a[1].snctn);
  }, [works, workTypes]);

  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (s === 0 && !admn_snctn_nm.trim()) newErrors.admn_snctn_nm = 'Title is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) { showError('Please fill required fields'); return false; }
    return true;
  };

  const goNext = async () => {
    if (!validateStep(step)) return;
    /* Persist any funding edits before the step unmounts. A failed write keeps the
     * user on the step rather than advancing over lost data. */
    if (!(await flushFundingPattern())) return;
    setStep(prev => Math.min(prev + 1, STEPS.length - 1));
  };
  const goBack = () => setStep(prev => Math.max(prev - 1, 0));

  const handleSave = async () => {
    /* Flush funding edits first: saving the sanction while the split sat unsaved in
     * the editor is exactly how the values were being lost. */
    if (!(await flushFundingPattern())) return;
    if (!validateStep(0)) { setStep(0); return; }
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        admn_snctn_nm: admn_snctn_nm.trim(), dscn_tx: dscn_tx.trim() || null,
        fy_id: fy_id || null, snctn_auth_id: snctn_auth_id || null,
        go_nu: go_nu.trim() || null, go_dt: go_dt || null, go_doc_url: null,
        rsln_nu: rsln_nu.trim() || null, rsln_dt: rsln_dt || null,
        rmrks_tx: rmrks_tx.trim() || null,
        totl_prpsd_at: totalEstimatedCost, totl_snctn_at: totalSanctionedCost,
        iec_at: iec_at || 0, cb_at: cb_at || 0
      };
      let sanctionId: number | null = null;
      if (isEdit && editId) {
        await adminSanctionsApi.update(editId, payload);
        sanctionId = editId;
      } else {
        const result = await adminSanctionsApi.create(payload);
        sanctionId = result.as_id;
      }

      // Apply work rows: existing-work rows are linked (and amount/priority/remarks updated);
      // new (unbacked) rows are created with admn_snctn_id pointing at this sanction.
      if (sanctionId) {
        for (const work of works) {
          try {
            if (work.wrk_id) {
              // Existing work — link to AS and update only the editable fields
              await worksApi.update(work.wrk_id, {
                admn_snctn_id: sanctionId,
                orgnl_estmt_at: work.estmt_cst_at,
                snctn_at: work.snctn_at || work.estmt_cst_at,
                prty_nu: work.prty_nu,
                rmrks_tx: work.snctn_rmrks_tx || null
              } as any);
            } else if (work.wrk_nm.trim()) {
              await worksApi.create({
                wrk_nm: work.wrk_nm.trim(), wrk_type_id: work.wrk_type_id,
                wrk_ctgry_id: work.wrk_ctgry_id, schme_id: work.schme_id,
                orgnl_estmt_at: work.estmt_cst_at, snctn_at: work.snctn_at || work.estmt_cst_at,
                admn_snctn_id: sanctionId, fy_id: fy_id,
                prty_nu: work.prty_nu, rmrks_tx: work.snctn_rmrks_tx || null
              } as any);
            }
          } catch (e) { console.error('Error saving work row:', e); }
        }
      }
      showSuccess(isEdit ? 'Admin Sanction updated successfully' : 'Admin Sanction created successfully');
      onSuccess(); onClose();
    } catch (error: any) {
      console.error('Error saving admin sanction:', error);
      showError(error.response?.data?.message || 'Failed to save admin sanction');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-purple-50/30';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[9999]">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>
      <div className={`flex min-h-full items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
        <div className={`relative bg-white shadow-xl transform transition-all ${
          isFullscreen ? 'w-full h-screen rounded-none' : 'w-full max-w-5xl rounded-xl'
        }`}>
          {/* Header */}
          <div className={`flex-shrink-0 text-white border-b border-white/10 ${WMS_POPUP_GRADIENT} ${isFullscreen ? '' : 'rounded-t-xl'}`}>
            <div className="flex items-center justify-between gap-3 px-4 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className={WMS_POPUP_ICON}>
                  <i className="ri-government-line"></i>
                </div>
                <div className="min-w-0">
                  <h2 className={WMS_POPUP_TITLE}>{isEdit ? 'Edit Admin Sanction' : 'New Admin Sanction'}</h2>
                  <p className={WMS_POPUP_SUBTITLE}>{isEdit ? `Editing: ${admn_snctn_nm || ''}` : 'Create a new administrative sanction with works'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setIsFullscreen(!isFullscreen)} className={WMS_POPUP_CLOSE}>
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
                </button>
                <button onClick={onClose} className={WMS_POPUP_CLOSE}>
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center px-4 pb-1.5 gap-1">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center flex-1">
                  <button
                    onClick={() => { if (i < step || validateStep(step)) setStep(i); }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all w-full ${
                      i === step ? 'bg-white/25 text-white' : i < step ? 'bg-white/10 text-white/80 hover:bg-white/15' : 'text-white/40'
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                      i === step ? 'bg-white text-teal-700' : i < step ? 'bg-white/30 text-white' : 'bg-white/10 text-white/40'
                    }`}>{i < step ? <i className="ri-check-line"></i> : i + 1}</span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </button>
                  {i < STEPS.length - 1 && <div className={`w-6 h-0.5 flex-shrink-0 ${i < step ? 'bg-white/40' : 'bg-white/10'}`}></div>}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className={`p-6 overflow-y-auto bg-gray-100 ${isFullscreen ? 'h-[calc(100vh-180px)]' : 'max-h-[65vh]'}`}>
            {loading ? (
              <ListSkeleton />
            ) : (
              <>
                {/* Step 1: Sanction Details */}
                {step === 0 && (
                  <div className="space-y-6">
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-file-list-3-line text-purple-600"></i> Basic Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className={labelCls}>Title <span className="text-red-500">*</span></label>
                          <input type="text" value={admn_snctn_nm}
                            onChange={(e) => { setTitle(e.target.value); if (errors.admn_snctn_nm) setErrors(prev => ({ ...prev, admn_snctn_nm: '' })); }}
                            className={`${inputCls} ${errors.admn_snctn_nm ? 'border-red-500 bg-red-50/30' : ''}`}
                            placeholder="Enter admin sanction title" />
                          {errors.admn_snctn_nm && <p className="text-red-500 text-xs mt-1"><i className="ri-error-warning-line mr-1"></i>{errors.admn_snctn_nm}</p>}
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Description</label>
                          <textarea value={dscn_tx} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputCls} placeholder="Description..." />
                        </div>
                        <div>
                          <label className={labelCls}>Financial Year</label>
                          <select value={fy_id || ''} onChange={(e) => setFyId(parseInt(e.target.value) || null)} className={inputCls}>
                            <option value="">Select Financial Year</option>
                            {financialYears.map(fy => <option key={fy.fy_id} value={fy.fy_id}>{fy.fy_nm}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Sanction Authority</label>
                          <select value={snctn_auth_id || ''} onChange={(e) => setSanctionAuthId(parseInt(e.target.value) || null)} className={inputCls}>
                            <option value="">Select Authority</option>
                            {authorities.map(a => <option key={a.snctn_auth_id} value={a.snctn_auth_id}>{a.snctn_auth_nm}</option>)}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Scheme</label>
                          <select value={schme_id || ''} onChange={(e) => setSchmeId(parseInt(e.target.value) || null)} className={inputCls}>
                            <option value="">Select Scheme</option>
                            {schemes.map(s => <option key={s.schme_id} value={s.schme_id}>{s.schme_nm}</option>)}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">Default scheme for all works</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-file-paper-2-line text-purple-600"></i> GO Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={labelCls}>GO Number</label>
                          <input type="text" value={go_nu} onChange={(e) => setGoNu(e.target.value)} className={inputCls} placeholder="Enter GO Number" />
                        </div>
                        <div>
                          <label className={labelCls}>GO Date</label>
                          <input type="date" value={go_dt} onChange={(e) => setGoDt(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>GO Document</label>
                          <input type="file" onChange={(e) => setGoDocFile(e.target.files?.[0] || null)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 bg-purple-50/30 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-purple-100 file:text-purple-700"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                        </div>
                        <div>
                          <label className={labelCls}>Council Resolution No</label>
                          <input type="text" value={rsln_nu} onChange={(e) => setRslnNu(e.target.value)} className={inputCls} placeholder="Resolution No" />
                        </div>
                        <div>
                          <label className={labelCls}>Resolution Date</label>
                          <input type="date" value={rsln_dt} onChange={(e) => setRslnDt(e.target.value)} className={inputCls} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Works */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <i className="ri-building-2-line text-purple-600"></i>
                        Works ({works.length})
                        {totalSanctionedCost > 0 && <span className="text-xs font-normal text-gray-500 ml-2">Total: {fmtINR(totalSanctionedCost)}</span>}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={openPicker}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-600 text-purple-700 text-sm rounded-lg hover:bg-purple-50 transition-colors"
                          title="Pick from existing works/estimates that have no admin sanction yet">
                          <i className="ri-checkbox-multiple-line"></i> Pick Existing
                        </button>
                        <button type="button" onClick={addWork}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors">
                          <i className="ri-add-line"></i> Add Work
                        </button>
                      </div>
                    </div>

                    {works.length === 0 ? (
                      <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
                        <i className="ri-building-2-line text-5xl text-gray-300 mb-3"></i>
                        <p className="text-gray-500">No works added yet</p>
                        <div className="mt-3 flex items-center justify-center gap-2">
                          <button onClick={openPicker} className="px-4 py-2 bg-white border border-purple-600 text-purple-700 rounded-lg text-sm hover:bg-purple-50">
                            <i className="ri-checkbox-multiple-line mr-1"></i> Pick Existing
                          </button>
                          <button onClick={addWork} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200">
                            <i className="ri-add-line mr-1"></i> Add First Work
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {works.map((work, index) => {
                          const isExisting = !!work.wrk_id;
                          const typeNm = work.wrk_type_nm || workTypes.find(t => t.wrk_type_id === work.wrk_type_id)?.wrk_type_nm;
                          const ctgryNm = workCategories.find(c => c.wrk_ctgry_id === work.wrk_ctgry_id)?.wrk_ctgry_nm;
                          const schmeNm = work.schme_nm || schemes.find(s => s.schme_id === work.schme_id)?.schme_nm;
                          return (
                          <div key={work.id} className={`p-4 rounded-lg border shadow-sm ${isExisting ? 'bg-indigo-50/40 border-indigo-200' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-purple-700 flex items-center gap-2">
                                <i className="ri-hashtag"></i>Work {index + 1}
                                {isExisting && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                                    <i className="ri-link"></i> Linked existing work
                                  </span>
                                )}
                                {isExisting && work.wrk_cd && (
                                  <span className="font-mono text-[10px] bg-white border border-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded">{work.wrk_cd}</span>
                                )}
                              </span>
                              <button type="button" onClick={() => removeWork(work.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors" title={isExisting ? 'Unlink from this admin sanction' : 'Remove work'}>
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                            {isExisting ? (
                              <>
                                {/* Read-only existing work context */}
                                <div className="bg-white border border-indigo-100 rounded-lg p-3 mb-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                  <div className="md:col-span-2">
                                    <span className="text-[10px] uppercase tracking-wide text-gray-500">Work Name</span>
                                    <div className="text-sm text-gray-900">{work.wrk_nm}</div>
                                  </div>
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wide text-gray-500">Work Type/Sector</span>
                                    <div className="text-gray-800">{typeNm || '-'}</div>
                                  </div>
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wide text-gray-500">Work Category</span>
                                    <div className="text-gray-800">{ctgryNm || '-'}</div>
                                  </div>
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wide text-gray-500">Scheme</span>
                                    <div className="text-gray-800">{schmeNm || '-'}</div>
                                  </div>
                                  <div className="flex flex-wrap gap-2 text-gray-500">
                                    {work.ulb_nm && <span><i className="ri-building-line mr-1"></i>{work.ulb_nm}</span>}
                                    {work.fy_cd && <span><i className="ri-calendar-line mr-1"></i>{work.fy_cd}</span>}
                                    {work.estmt_cd && <span className="text-emerald-700"><i className="ri-file-list-3-line mr-1"></i>{work.estmt_cd}</span>}
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Amount</label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                      <input type="number" value={work.estmt_cst_at || ''} onChange={(e) => updateWork(work.id, 'estmt_cst_at', parseFloat(e.target.value) || 0)}
                                        className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="0" min="0" />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Sanctioned Amount</label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                      <input type="number" value={work.snctn_at || ''} onChange={(e) => updateWork(work.id, 'snctn_at', parseFloat(e.target.value) || 0)}
                                        className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="0" min="0" />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Priority No</label>
                                    <input type="number" value={work.prty_nu ?? ''} onChange={(e) => updateWork(work.id, 'prty_nu', parseInt(e.target.value) || null)}
                                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      placeholder="e.g. 1" min="1" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                                    <textarea value={work.snctn_rmrks_tx} onChange={(e) => updateWork(work.id, 'snctn_rmrks_tx', e.target.value)} rows={2}
                                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      placeholder="Remarks" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div className="md:col-span-4">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Work Name</label>
                                  <textarea value={work.wrk_nm} onChange={(e) => updateWork(work.id, 'wrk_nm', e.target.value)} rows={2}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="Enter work name" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Work Type/Sector</label>
                                  <select value={work.wrk_type_id || ''} onChange={(e) => updateWork(work.id, 'wrk_type_id', parseInt(e.target.value) || null)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                                    <option value="">Select Type</option>
                                    {workTypes.map(t => <option key={t.wrk_type_id} value={t.wrk_type_id}>{t.wrk_type_nm}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Work Category</label>
                                  <select value={work.wrk_ctgry_id || ''} onChange={(e) => updateWork(work.id, 'wrk_ctgry_id', parseInt(e.target.value) || null)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                                    <option value="">Select Category</option>
                                    {workCategories.map(c => <option key={c.wrk_ctgry_id} value={c.wrk_ctgry_id}>{c.wrk_ctgry_nm}</option>)}
                                  </select>
                                </div>
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Scheme</label>
                                  <select value={work.schme_id || ''} onChange={(e) => updateWork(work.id, 'schme_id', parseInt(e.target.value) || null)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                                    <option value="">Select Scheme</option>
                                    {schemes.map(s => <option key={s.schme_id} value={s.schme_id}>{s.schme_nm}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Amount</label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                    <input type="number" value={work.estmt_cst_at || ''} onChange={(e) => updateWork(work.id, 'estmt_cst_at', parseFloat(e.target.value) || 0)}
                                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      placeholder="0" min="0" />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Sanctioned Amount</label>
                                  <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                    <input type="number" value={work.snctn_at || ''} onChange={(e) => updateWork(work.id, 'snctn_at', parseFloat(e.target.value) || 0)}
                                      className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                      placeholder="0" min="0" />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority No</label>
                                  <input type="number" value={work.prty_nu ?? ''} onChange={(e) => updateWork(work.id, 'prty_nu', parseInt(e.target.value) || null)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="e.g. 1" min="1" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                                  <textarea value={work.snctn_rmrks_tx} onChange={(e) => updateWork(work.id, 'snctn_rmrks_tx', e.target.value)} rows={2}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="Remarks" />
                                </div>
                              </div>
                            )}
                          </div>
                          );
                        })}

                        {/* Works Total */}
                        <div className="flex items-center justify-between bg-purple-100 px-4 py-3 rounded-lg">
                          <span className="text-sm font-medium text-purple-800">{works.length} work{works.length !== 1 ? 's' : ''}</span>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <div className="text-xs text-purple-600">Estimated</div>
                              <div className="font-bold text-purple-900">{fmtINR(totalEstimatedCost)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-purple-600">Sanctioned</div>
                              <div className="font-bold text-purple-900">{fmtINR(totalSanctionedCost)}</div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 3: Funding Pattern */}
                {step === 2 && (
                  <div className="space-y-4">
                    {works.filter(w => w.id && w.wrk_nm).length > 0 ? (
                      <FundingPatternEditor
                        ref={fundingRef}
                        works={works.filter(w => w.id && w.wrk_nm).map(w => ({
                          // w.id is the row key (the wrk_id for a linked work), NOT the
                          // work code — passing it as wrk_cd showed the raw id "616"
                          // instead of WRK/2026-27/0004.
                          wrk_id: w.wrk_id ?? (parseInt(w.id) || 0),
                          wrk_cd: w.wrk_cd || w.id,
                          wrk_nm: w.wrk_nm,
                          // The editor renders a UDA column; it was never given one.
                          ulb_nm: w.ulb_nm,
                          // Nothing in the lifecycle rolls up dswms_wrk_lst_t.snctn_at —
                          // it is still 0 on every work — so this column always read 0.
                          // The amount being sanctioned is the estimate.
                          as_at: w.snctn_at || w.estmt_cst_at || 0,
                        }))}
                      />
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <i className="ri-money-rupee-circle-line text-4xl"></i>
                        <p className="mt-2">Add works in the previous step to configure funding pattern</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 4: Allocation & Review */}
                {step === 3 && (
                  <div className="space-y-6">
                    {/* IEC & CB Allocation */}
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-percent-line text-purple-600"></i> IEC & Capacity Building Allocation
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                              <i className="ri-megaphone-line text-blue-600 text-sm"></i>
                            </div>
                            <span className="text-sm font-semibold text-blue-800">IEC Activities</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Percentage (%)</label>
                              <input type="number" value={iec_pct} onChange={(e) => setIecPct(parseFloat(e.target.value) || 0)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                min="0" max="100" step="0.5" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                <input type="number" value={iec_at || ''} onChange={(e) => setIecAt(parseFloat(e.target.value) || 0)}
                                  className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                                  min="0" />
                              </div>
                            </div>
                          </div>
                          {totalSanctionedCost > 0 && (
                            <p className="text-xs text-blue-600 mt-2">{iec_pct}% of {fmtINR(totalSanctionedCost)} = {fmtINR(Math.round(totalSanctionedCost * iec_pct / 100))}</p>
                          )}
                        </div>
                        <div className="bg-green-50/50 p-4 rounded-lg border border-green-100">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center">
                              <i className="ri-team-line text-green-600 text-sm"></i>
                            </div>
                            <span className="text-sm font-semibold text-green-800">Capacity Building</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Percentage (%)</label>
                              <input type="number" value={cb_pct} onChange={(e) => setCbPct(parseFloat(e.target.value) || 0)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                                min="0" max="100" step="0.5" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500 text-sm">&#8377;</span>
                                <input type="number" value={cb_at || ''} onChange={(e) => setCbAt(parseFloat(e.target.value) || 0)}
                                  className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-green-500"
                                  min="0" />
                              </div>
                            </div>
                          </div>
                          {totalSanctionedCost > 0 && (
                            <p className="text-xs text-green-600 mt-2">{cb_pct}% of {fmtINR(totalSanctionedCost)} = {fmtINR(Math.round(totalSanctionedCost * cb_pct / 100))}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Remarks */}
                    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <i className="ri-chat-3-line text-purple-600"></i> Remarks
                      </h3>
                      <textarea value={rmrks_tx} onChange={(e) => setRmrksTx(e.target.value)} rows={3} className={inputCls} placeholder="Any additional remarks..." />
                    </div>

                    {/* Review Summary */}
                    <div className="bg-white p-5 rounded-lg border border-purple-200 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <i className="ri-bar-chart-line text-purple-600"></i> Summary
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-gray-50 p-3 rounded-lg text-center">
                          <div className="text-2xl font-bold text-gray-900">{works.length}</div>
                          <div className="text-xs text-gray-500">Works</div>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg text-center">
                          <div className="text-sm font-bold text-purple-800">{fmtINR(totalSanctionedCost)}</div>
                          <div className="text-xs text-purple-600">Sanctioned</div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-lg text-center">
                          <div className="text-sm font-bold text-blue-800">{fmtINR(iec_at)}</div>
                          <div className="text-xs text-blue-600">IEC ({iec_pct}%)</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg text-center">
                          <div className="text-sm font-bold text-green-800">{fmtINR(cb_at)}</div>
                          <div className="text-xs text-green-600">CB ({cb_pct}%)</div>
                        </div>
                      </div>

                      {/* Sector-wise breakdown */}
                      {sectorSummary.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sector-wise Breakdown</h4>
                          <div className="space-y-1.5">
                            {sectorSummary.map(([name, data]) => (
                              <div key={name} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded">
                                <span className="text-sm text-gray-700">{name} <span className="text-gray-400">({data.count})</span></span>
                                <span className="text-sm font-medium text-gray-900">{fmtINR(data.snctn)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 pt-3 border-t border-gray-100">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-gray-700">Grand Total (Works + IEC + CB)</span>
                          <span className="text-lg font-bold text-purple-800">{fmtINR(totalSanctionedCost + iec_at + cb_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer with step navigation */}
          <div className={`border-t px-6 py-4 bg-gray-50 flex items-center justify-between ${isFullscreen ? '' : 'rounded-b-xl'}`}>
            <div>
              {step > 0 ? (
                <button onClick={goBack} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5">
                  <i className="ri-arrow-left-line"></i> Previous
                </button>
              ) : (
                <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step < STEPS.length - 1 ? (
                <button onClick={goNext}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1.5">
                  Next <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button onClick={handleSave} disabled={saving}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : <><i className="ri-check-line"></i> {isEdit ? 'Update Sanction' : 'Create Sanction'}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pick Existing Works (no admin sanction yet) */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setPickerOpen(false)}></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>
              <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-checkbox-multiple-line text-base text-white/90"></i>
                  <div className="min-w-0">
                    <h2 className={WMS_POPUP_TITLE}>Pick Existing Works</h2>
                    <p className={WMS_POPUP_SUBTITLE}>Works without an admin sanction; estimate info shown when present</p>
                  </div>
                </div>
                <button onClick={() => setPickerOpen(false)} className={WMS_POPUP_CLOSE}>
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="px-6 py-3 border-b space-y-2">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder="Search by work code, name, UDA, scheme, or estimate code..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      autoFocus
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600 whitespace-nowrap">
                    <input type="checkbox" checked={pickerOnlyWithEstimate} onChange={(e) => setPickerOnlyWithEstimate(e.target.checked)} className="accent-purple-600" />
                    Only with estimate
                  </label>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {filteredPickerWorks.length} of {pickerWorks.length}
                    {pickerSelected.size > 0 && <span className="ml-2 text-purple-700 font-medium">· {pickerSelected.size} selected</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={pickerFy} onChange={(e) => setPickerFy(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="">All Years</option>
                    {financialYears.map((fy: any) => <option key={fy.fy_id} value={fy.fy_id}>{fy.fy_nm || fy.fy_cd}</option>)}
                  </select>
                  <select value={pickerSchme} onChange={(e) => setPickerSchme(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="">All Schemes</option>
                    {schemes.map((s: any) => <option key={s.schme_id} value={s.schme_id}>{s.schme_nm}</option>)}
                  </select>
                  <select value={pickerType} onChange={(e) => setPickerType(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="">All Work Types/Sectors</option>
                    {workTypes.map((t: any) => <option key={t.wrk_type_id} value={t.wrk_type_id}>{t.wrk_type_nm}</option>)}
                  </select>
                  <select value={pickerUda} onChange={(e) => setPickerUda(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="border rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                    <option value="">All UDAs</option>
                    {pickerUlbOptions.map((u) => <option key={u.id} value={u.id}>{u.nm}</option>)}
                  </select>
                  {pickerFiltersActive && (
                    <button type="button" onClick={clearPickerFilters}
                      className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1">
                      <i className="ri-close-line"></i> Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-auto">
                {pickerLoading ? (
                  <TableSkeleton />
                ) : filteredPickerWorks.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-inbox-line text-4xl mb-2 block opacity-30"></i>
                    <p className="font-medium">No matching works</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left w-10">
                          <input type="checkbox"
                            checked={
                              filteredPickerWorks.filter((w) => !works.some((r) => r.id === String(w.wrk_id))).length > 0 &&
                              filteredPickerWorks
                                .filter((w) => !works.some((r) => r.id === String(w.wrk_id)))
                                .every((w) => pickerSelected.has(w.wrk_id))
                            }
                            onChange={togglePickerAll}
                            className="accent-purple-600"
                          />
                        </th>
                        <th className="px-3 py-2 text-left">Work</th>
                        <th className="px-3 py-2 text-left">UDA / Scheme / FY</th>
                        <th className="px-3 py-2 text-right">Estimate</th>
                        <th className="px-3 py-2 text-right">Sanction Amt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPickerWorks.map((w) => {
                        const alreadyAdded = works.some((r) => r.id === String(w.wrk_id));
                        const checked = pickerSelected.has(w.wrk_id);
                        return (
                          <tr
                            key={w.wrk_id}
                            className={`border-t ${alreadyAdded ? 'opacity-50 bg-gray-50' : checked ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-3 py-2">
                              <input type="checkbox" checked={checked} disabled={alreadyAdded}
                                onChange={() => togglePickerRow(w.wrk_id)}
                                className="accent-purple-600" />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-mono text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{w.wrk_cd}</span>
                                {alreadyAdded && <span className="text-[10px] text-gray-500 italic">already added</span>}
                              </div>
                              <div className="text-gray-900 truncate max-w-md" title={w.wrk_nm}>{w.wrk_nm}</div>
                              {w.wrk_type_nm && <div className="text-[10px] text-gray-400">{w.wrk_type_nm}</div>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">
                              <div>{w.ulb_nm || '-'}</div>
                              <div className="text-gray-400">{w.schme_nm || '-'} · {w.fy_cd || w.fy_nm || '-'}</div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {w.estmt_id ? (
                                <div>
                                  <div className="font-mono text-xs text-emerald-700">{w.estmt_cd}</div>
                                  <div className="text-xs text-gray-700">₹{Number(w.estmt_totl_at || 0).toLocaleString('en-IN')}</div>
                                  {w.estmt_sts_cd && <div className="text-[10px] text-gray-400">{w.estmt_sts_cd}</div>}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400 italic">no estimate</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs">
                              {w.snctn_at ? `₹${Number(w.snctn_at).toLocaleString('en-IN')}` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
                <button type="button" onClick={() => setPickerOpen(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="button" onClick={confirmPicker} disabled={pickerSelected.size === 0}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  Add {pickerSelected.size > 0 ? `(${pickerSelected.size})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
